import { Button } from "@/components/UI/Button";
import useNotificationStore, {
  NotificationMessage,
} from "@/store/notificationStore";
import useReleaseNotesStore from "@/store/releaseNotesStore";
import tradeDataStore from "@/store/tradeDataStore";
import useModalStore from "@/store/modalStore";
import { getStatusColor } from "@/lib/notificationConfig";
import { useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import { cubeCustomColors } from "../../../../tailwind.config";

// Bell icon SVG
export const formatBadgeCount = (count: number): string => {
  if (count >= 100) return "99+";
  return String(count);
};

// LQC-1833 — dismissing a notification must persist the release-notes seen date,
// else the item is re-injected as new on every login.
export const containsReleaseNotesNotification = (
  list: NotificationMessage[],
): boolean => list.some((n) => n.type === "ReleaseNotes");

const BellIcon = ({
  hasUnread,
  count,
}: {
  hasUnread: boolean;
  count: number;
}) => (
  <div className="relative">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="h-6 w-6 "
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
      />
    </svg>
    {hasUnread && (
      <span className={`absolute -right-2 -top-2.5 flex h-5 w-5 items-center justify-center rounded-full ${formatBadgeCount(count) == "99+" ? "text-[9px]" : "text-[12px]"} bg-orange-500   font-bold text-gray-700`}>
        {formatBadgeCount(count)}
      </span>
    )}
  </div>
);

export default function NotificationCenter({ theme }: { theme: any }) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const notifications = useNotificationStore((state) => state.notifications);
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const markAllAsRead = useNotificationStore((state) => state.markAllAsRead);
  const clearAllNotifications = useNotificationStore(
    (state) => state.clearAllNotifications,
  );
  const removeNotification = useNotificationStore(
    (state) => state.removeNotification,
  );
  const markReleaseNotesAsSeen = useReleaseNotesStore(
    (state) => state.markAsSeen,
  );
  const updateEisdsToFlash = tradeDataStore(
    (state) => state.updateEisdsToFlash,
  );
  const requestOpenSettings = useModalStore(
    (state) => state.requestOpenSettings,
  );

  useEffect(() => {
    function syncBadge() {
      if (unreadCount > 0) {
        if (unreadCount >= 100) {
          navigator.setAppBadge?.();
        } else {
          navigator.setAppBadge?.(unreadCount);
        }
      } else {
        navigator.clearAppBadge?.();
      }
    }

    syncBadge();
    document.addEventListener("visibilitychange", syncBadge);
    return () => document.removeEventListener("visibilitychange", syncBadge);
  }, [unreadCount]);

  const handleTooltipClick = () => {
    setIsTooltipOpen((prev) => !prev);
    if (!isTooltipOpen && unreadCount > 0) {
      markAllAsRead();
      if (containsReleaseNotesNotification(notifications)) {
        markReleaseNotesAsSeen(); // LQC-1833
      }
    }
  };

  const handleClearAll = () => {
    if (containsReleaseNotesNotification(notifications)) {
      markReleaseNotesAsSeen(); // LQC-1833
    }
    clearAllNotifications();
  };

  const handleRemoveNotification = (notification: NotificationMessage) => {
    if (notification.type === "ReleaseNotes") {
      markReleaseNotesAsSeen(); // LQC-1833
    }
    removeNotification(notification.id);
  };

  

  const handleFlashTrade = (tradeId: string) => {
    updateEisdsToFlash([tradeId]);
  };

  const handleNotificationAction = (notification: NotificationMessage) => {
    if (notification.type === "ReleaseNotes") {
      requestOpenSettings();
      setIsTooltipOpen(false);
    } else {
      handleFlashTrade(notification.tradeId);
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return new Date(date).toLocaleDateString();
  };

  const getTypeColor = (type: string, role?: string): string => {
    return getStatusColor(type, role);
  };

  const renderNotificationItem = (notification: NotificationMessage) => (
    <div
      key={notification.id}
      className={`group cursor-pointer px-4 py-3 transition-colors hover:bg-gray-600/30 ${
        !notification.read ? "bg-gray-600/10" : ""
      }`}
      onClick={() => handleNotificationAction(notification)}
    >
      <div className="flex items-start space-x-3">
        {notification.type === "ReleaseNotes" ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-400"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z"
            />
          </svg>
        ) : (
          <div
            className="mt-1 h-2 w-2 flex-shrink-0 rounded-full"
            style={{ backgroundColor: getTypeColor(notification.type, notification.role) }}
          />
        )}
        <div className="flex-1">
          <p className="text-sm text-black dark:text-white">
            {notification.message}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {formatTime(notification.timestamp)}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center">
          {/* Close icon */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveNotification(notification);
            }}
            className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-600/50 hover:text-white group-hover:opacity-100"
            aria-label="Remove notification"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-4 w-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="flex w-[35px] items-center justify-center"
      data-testid="NotificationCenterContainer"
      id="NotificationCenterContainer"
    >
      <div
        className="flex w-8 cursor-pointer items-center justify-center"
        id="notificationIconContainer"
        onClick={handleTooltipClick}
      >
        <div
          data-tooltip-id="notifications"
          className="text-cubeBase-600 hover:text-orange-600 dark:text-cubeBaseDark-600 dark:hover:text-orange-400"
        >
          <BellIcon hasUnread={unreadCount > 0} count={unreadCount} />
        </div>
      </div>

      <Tooltip
        id="notifications"
        place="right"
        variant="info"
        className="z-40"
        opacity={0.95}
        clickable={true}
        openOnClick={true}
        isOpen={isTooltipOpen}
        setIsOpen={setIsTooltipOpen}
        globalCloseEvents={{ clickOutsideAnchor: true, escape: true }}
        border="1px solid gray"
        style={{
          backgroundColor:
            theme === "dark"
              ? cubeCustomColors.cubeBaseDark[700]
              : cubeCustomColors.cubeBase[700],
          padding: 0,
          transform: "translateY(-10px)",
        }}
      >
        <div className="flex h-[466px] w-[350px] flex-col">
          {/* Header */}
          <div className="flex-shrink-0 border-b border-gray-600 px-4 py-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-black dark:text-white">
                Notifications
              </h3>
              {notifications.length > 0 && (
                <Button
                  variant="icon"
                  onClick={handleClearAll}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  Clear All
                </Button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="mb-2 h-12 w-12 opacity-50"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
                  />
                </svg>
                <p className="text-sm">No notifications yet</p>
                <p className="mt-1 text-xs">Trade updates will appear here</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-600">
                {notifications.map(renderNotificationItem)}
              </div>
            )}
          </div>
        </div>
      </Tooltip>
    </div>
  );
}
